import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Chip,
  Button,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Paper,
  Tooltip,
  useTheme,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PeopleIcon from '@mui/icons-material/People';
import BadgeIcon from '@mui/icons-material/Badge';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InventoryIcon from '@mui/icons-material/Inventory2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import CompetenciaSelector, {
  formatCompetencia,
  formatCompetenciaLonga,
  getCompetenciaInicial,
  saveCompetencia,
} from '../../components/saude/CompetenciaSelector';

const TODAS = 'todas';

const num = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');
const formatDateBr = (iso?: string | null): string =>
  iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : '-';

interface KpiProps {
  icon: React.ReactNode;
  label: string;
  valor: React.ReactNode;
  cor: string;
  hint?: string;
}

const Kpi: React.FC<KpiProps> = ({ icon, label, valor, cor, hint }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ textAlign: 'center' }}>
        <Box sx={{ color: cor }}>{icon}</Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
          {hint && (
            <Tooltip title={hint}>
              <InfoOutlinedIcon sx={{ fontSize: 13, ml: 0.4, verticalAlign: 'middle', opacity: 0.6 }} />
            </Tooltip>
          )}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ color: cor }}>
          {valor}
        </Typography>
      </CardContent>
    </Card>
  );
};

/**
 * Relatorio "Fichas em Rascunho".
 *
 * Fichas que o profissional digitou no sistema e nunca finalizou: nao sao enviadas, nao entram
 * em nenhum indicador e nao contam para o cofinanciamento. A tela mostra unidade e profissional
 * para a gestao cobrar a finalizacao.
 *
 * Sem jargao tecnico na UI (nada de e-SUS, CDS, `st_ficha` ou `tb_fat`) — quem le e o gestor.
 */
const SaudeFichasRascunho: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [competencia, setCompetencia] = useState(getCompetenciaInicial());
  const [tipo, setTipo] = useState<string>(TODAS);

  const unidadeParam = unidadeSelecionada?.codunidade;
  const tipoParam = tipo !== TODAS ? tipo : undefined;

  /**
   * Teto do seletor = mes CORRENTE, nao a ultima competencia com rascunho.
   *
   * Aqui competencia vazia e a BOA noticia — o gestor precisa poder abrir o mes atual justamente
   * para confirmar que ninguem deixou ficha pendente. Travar no ultimo mes com pendencia
   * esconderia essa consulta.
   */
  const competenciaMaxima = useMemo(() => {
    const h = new Date();
    return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    saudeService
      .getFichasRascunho(municipioAtivo.id, competencia, tipoParam, unidadeParam)
      .then((res: any) => setData(res))
      .catch((err: any) =>
        setError(err.response?.data?.message || 'Erro ao carregar o relatorio de fichas em rascunho.'),
      )
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, competencia, tipoParam, unidadeParam]);

  const trocarCompetencia = useCallback((nova: string) => {
    setCompetencia(nova);
    saveCompetencia(nova);
  }, []);

  const exportarCsv = useCallback(() => {
    if (!municipioAtivo?.id) return;
    saudeService
      .exportarFichasRascunhoCsv(municipioAtivo.id, competencia, tipoParam, unidadeParam)
      .catch(() => setError('Falha ao exportar CSV.'));
  }, [municipioAtivo?.id, competencia, tipoParam, unidadeParam]);

  const exportarPdf = useCallback(() => {
    if (!municipioAtivo?.id) return;
    saudeService
      .gerarFichasRascunhoPdf(municipioAtivo.id, competencia, tipoParam, unidadeParam)
      .catch(() => setError('Falha ao exportar PDF.'));
  }, [municipioAtivo?.id, competencia, tipoParam, unidadeParam]);

  if (!municipioAtivo?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Selecione um municipio para acessar o relatorio.</Alert>
      </Box>
    );
  }

  const fichas: any[] = data?.fichas || [];
  const resumo = data?.resumo || {};
  const porTipo: any[] = data?.por_tipo || [];
  const tiposComErro: string[] = data?.tipos_com_erro || [];
  const apurado = !!data && data.dwh_implementado !== false && !data.competencia_sem_rascunho;
  // Microarea so existe na visita domiciliar: a coluna aparece apenas quando ha valor.
  const temMicroarea = fichas.some((f) => !!f.microareas);
  // Opcoes do seletor: o payload traz os 10 tipos com a contagem da competencia, para o tipo sem
  // pendencia aparecer com 0 em vez de desaparecer.
  const opcoesTipo = porTipo.length > 0 ? porTipo : (data?.tipos_disponiveis || []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            <EditNoteIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#e65100' }} />
            Fichas em Rascunho
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fichas registradas e ainda nao finalizadas pelo profissional
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={exportarCsv}
            disabled={loading || !apurado || fichas.length === 0}
          >
            CSV
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportarPdf}
            disabled={loading || !apurado}
          >
            PDF
          </Button>
          <CompetenciaSelector
            competencia={competencia}
            onChange={trocarCompetencia}
            maxCompetencia={competenciaMaxima}
            disabled={loading}
          />
        </Box>
      </Box>

      {/* Escopo e filtro por tipo de ficha */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Chip
          icon={<LocalHospitalIcon sx={{ fontSize: 16 }} />}
          label={unidadeSelecionada ? unidadeSelecionada.nome : 'Todas as unidades do municipio'}
          variant="outlined"
          color={unidadeSelecionada ? 'default' : 'primary'}
        />
        <TextField
          select
          size="small"
          label="Tipo de ficha"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          disabled={loading}
          sx={{ minWidth: 280 }}
        >
          <MenuItem value={TODAS}>Todas as fichas</MenuItem>
          {opcoesTipo.map((t: any) => (
            <MenuItem key={t.slug} value={t.slug}>
              {t.label}
              {t.fichas !== undefined && ` (${num(t.fichas)})`}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : data?.dwh_implementado === false ? (
        <Alert severity="info">
          <AlertTitle>Nao disponivel para este municipio</AlertTitle>
          {data.motivo ||
            'Este municipio le os indicadores do Data Warehouse. O relatorio de fichas em rascunho esta implementado apenas para conexao direta ao sistema do municipio.'}
        </Alert>
      ) : data ? (
        <>
          {tiposComErro.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nao foi possivel consultar {tiposComErro.length} tipo(s) de ficha nesta base:{' '}
              {tiposComErro.join(', ')}. Os demais tipos foram apurados normalmente.
            </Alert>
          )}

          {/* KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Kpi
                icon={<EditNoteIcon />}
                label="Fichas em rascunho"
                valor={num(resumo.fichas)}
                cor={resumo.fichas > 0 ? '#e65100' : '#2e7d32'}
                hint="Fichas da competencia que o profissional ainda nao finalizou"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Kpi
                icon={<PeopleIcon />}
                label="Cidadaos afetados"
                valor={num(resumo.cidadaos)}
                cor={theme.palette.text.primary}
                hint="Atendimentos registrados nessas fichas que nao foram computados"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Kpi
                icon={<BadgeIcon />}
                label="Profissionais"
                valor={num(resumo.profissionais)}
                cor={theme.palette.text.primary}
                hint="Profissionais que precisam finalizar os registros"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Kpi
                icon={<LocalHospitalIcon />}
                label="Unidades"
                valor={num(resumo.unidades)}
                cor={theme.palette.text.primary}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Kpi
                icon={<HourglassEmptyIcon />}
                label="Mais antiga (dias)"
                valor={num(resumo.mais_antiga_dias)}
                cor={resumo.mais_antiga_dias > 90 ? '#c62828' : theme.palette.text.primary}
                hint="Dias desde a data da ficha mais antiga que continua em rascunho"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Kpi
                icon={<InventoryIcon />}
                label="Total no municipio"
                valor={num(data.total_rascunhos_municipio)}
                cor={data.total_rascunhos_municipio > 0 ? '#c62828' : '#2e7d32'}
                hint="Todas as competencias e todos os tipos de ficha — o passivo acumulado, que o filtro mensal esconde"
              />
            </Grid>
          </Grid>

          {/* Competencia limpa: e a boa noticia, mas o passivo acumulado continua visivel */}
          {data.competencia_sem_rascunho ? (
            <Alert severity={data.total_rascunhos_municipio > 0 ? 'warning' : 'success'} icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
              <AlertTitle>
                Nenhuma ficha em rascunho em {formatCompetenciaLonga(data.competencia)}
              </AlertTitle>
              {data.total_rascunhos_municipio > 0 ? (
                <>
                  O municipio ainda tem <strong>{num(data.total_rascunhos_municipio)}</strong> ficha(s)
                  em rascunho em outras competencias.
                  {data.max_competencia && (
                    <>
                      {' '}
                      A mais recente esta em{' '}
                      <Button
                        size="small"
                        sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}
                        onClick={() => trocarCompetencia(data.max_competencia)}
                      >
                        <strong>{formatCompetencia(data.max_competencia)}</strong>
                      </Button>
                      .
                    </>
                  )}
                </>
              ) : (
                'Nao ha nenhuma ficha pendente de finalizacao no municipio.'
              )}
            </Alert>
          ) : (
            <>
              {/* Detalhe ficha a ficha */}
              <Paper
                elevation={0}
                sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3, overflow: 'hidden' }}
              >
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Fichas em rascunho em {formatCompetenciaLonga(data.competencia)} ({fichas.length})
                  </Typography>
                </Box>
                <TableContainer sx={{ overflowX: 'auto', maxHeight: 560 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo de ficha</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell align="right">Dias</TableCell>
                        <TableCell>Profissional</TableCell>
                        <TableCell>Unidade</TableCell>
                        <TableCell>Equipe</TableCell>
                        {temMicroarea && <TableCell>Microarea</TableCell>}
                        <TableCell align="right">Cidadaos</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fichas.map((f: any) => (
                        <TableRow key={`${f.tipo}-${f.ficha}`} hover>
                          <TableCell>
                            <Chip label={f.tipo_label} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateBr(f.data_ficha)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: f.dias_em_rascunho > 90 ? 700 : 400,
                              color: f.dias_em_rascunho > 90 ? '#c62828' : undefined,
                            }}
                          >
                            {num(f.dias_em_rascunho)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {f.profissional}
                            {f.cbo && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                CBO {f.cbo}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{f.unidade || '-'}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{f.equipe || '-'}</TableCell>
                          {temMicroarea && <TableCell>{f.microareas || '-'}</TableCell>}
                          <TableCell align="right">
                            {f.nomes ? (
                              <Tooltip title={f.nomes}>
                                <Chip label={num(f.cidadaos)} size="small" />
                              </Tooltip>
                            ) : (
                              num(f.cidadaos)
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Por tipo de ficha */}
              {porTipo.some((t: any) => t.fichas > 0 || t.fichas_municipio > 0) && (
                <Accordion elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Por tipo de ficha
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tipo de ficha</TableCell>
                            <TableCell align="right">Fichas</TableCell>
                            <TableCell align="right">Cidadaos</TableCell>
                            <TableCell align="right">Mais antiga (dias)</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Total em aberto no municipio, somando todas as competencias">
                                <span>No municipio</span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {porTipo
                            .filter((t: any) => t.fichas > 0 || t.fichas_municipio > 0)
                            .map((t: any) => (
                              <TableRow key={t.slug} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{t.label}</TableCell>
                                <TableCell align="right">{num(t.fichas)}</TableCell>
                                {/* Cidadaos/dias so existem para o tipo detalhado; com filtro de
                                    tipo ativo, os outros mostram apenas a contagem de fichas. */}
                                <TableCell align="right">{t.detalhado ? num(t.cidadaos) : '-'}</TableCell>
                                <TableCell align="right">
                                  {t.detalhado ? num(t.mais_antiga_dias) : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                  {num(t.fichas_municipio)}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Por unidade */}
              {(data.por_unidade || []).length > 0 && (
                <Accordion elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Por unidade ({data.por_unidade.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Unidade</TableCell>
                            <TableCell align="right">Fichas</TableCell>
                            <TableCell align="right">Cidadaos</TableCell>
                            <TableCell align="right">Profissionais</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.por_unidade.map((u: any) => (
                            <TableRow key={u.nu_cnes || u.unidade} hover>
                              <TableCell sx={{ fontWeight: 600 }}>{u.unidade}</TableCell>
                              <TableCell align="right">{num(u.fichas)}</TableCell>
                              <TableCell align="right">{num(u.cidadaos)}</TableCell>
                              <TableCell align="right">{num(u.profissionais)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Por profissional — quem precisa finalizar */}
              {(data.por_profissional || []).length > 0 && (
                <Accordion elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Por profissional ({data.por_profissional.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer sx={{ overflowX: 'auto', maxHeight: 420 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Profissional</TableCell>
                            <TableCell>CBO</TableCell>
                            <TableCell>Unidade</TableCell>
                            <TableCell>Equipe</TableCell>
                            <TableCell align="right">Fichas</TableCell>
                            <TableCell align="right">Cidadaos</TableCell>
                            <TableCell align="right">Mais antiga (dias)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.por_profissional.map((p: any) => (
                            <TableRow key={p.cns || p.profissional} hover>
                              <TableCell sx={{ fontWeight: 600 }}>{p.profissional}</TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>{p.cbo || '-'}</TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>{p.unidade || '-'}</TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>{p.equipe || '-'}</TableCell>
                              <TableCell align="right">{num(p.fichas)}</TableCell>
                              <TableCell align="right">{num(p.cidadaos)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: p.mais_antiga_dias > 90 ? 700 : 400,
                                  color: p.mais_antiga_dias > 90 ? '#c62828' : undefined,
                                }}
                              >
                                {num(p.mais_antiga_dias)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )}

          {/* Competencias com pendencia — atalho para o passivo antigo */}
          {(data.competencias_com_rascunho || []).length > 0 && (
            <Accordion elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Competencias com fichas em rascunho ({data.competencias_com_rascunho.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {data.competencias_com_rascunho.map((c: any) => (
                    <Chip
                      key={c.competencia}
                      label={`${formatCompetencia(c.competencia)}: ${num(c.fichas)}`}
                      size="small"
                      variant={c.competencia === data.competencia ? 'filled' : 'outlined'}
                      color={c.competencia === data.competencia ? 'primary' : 'default'}
                      onClick={() => trocarCompetencia(c.competencia)}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Ressalvas */}
          {(data.limitacoes || []).length > 0 && (
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
              <AlertTitle sx={{ fontSize: '0.85rem' }}>Como este numero e apurado</AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: '0.8rem', mb: 0.3 } }}>
                {data.limitacoes.map((l: string, i: number) => (
                  <li key={i}>{l}</li>
                ))}
              </Box>
            </Alert>
          )}
        </>
      ) : null}
    </Box>
  );
};

export default SaudeFichasRascunho;
